import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode, faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { CharlieButton } from '@ver.id/libs.ui.charlie';
import { useTranslation } from '../../../i18n/i18n';
import { detectOs } from '../../../utils/platform';
import { useLayoutData } from '../../../hooks/useLayoutContext';
import HostedMobileLayout from '../../../layouts/MobileLayout';
import HostedActionRow from '../../../components/hosted/ActionRow';
import HostedQrCodeCard from '../../../components/hosted/QrCodeCard';
import AppLogo from '../../../components/shared/AppLogo';
import { useBackOrCancel } from '../../../hooks/useBackOrCancel';
import { appLinkFor } from '../../../utils/appLinks';
import { type AgentStartPageProps } from '../../../chulak/ChulakSession';
import { type FlowAction } from '../../../types';

/** Subtitle keys: sharing, then issuance. */
const SUBTITLES = {
  redirect: {
    share: 'agentStart.redirectSubtitle',
    issuance: 'agentStart.issuanceRedirectSubtitle',
  },
  qr: { share: 'agentStart.subtitle', issuance: 'agentStart.issuanceSubtitle' },
  openApp: {
    share: 'agentStart.mobileSubtitle',
    issuance: 'agentStart.issuanceMobileSubtitle',
  },
};

export default function HostedMobileAgentStartPage({
  tags,
  app,
  onCancel,
  onBack,
}: AgentStartPageProps) {
  const { t } = useTranslation();
  const appName = app?.nameWithArticle || app?.name || 'your identity app';
  const isIssuance = useLayoutData().requestType === 'issuance';
  const [showQrFallback, setShowQrFallback] = useState(false);
  const appLink = appLinkFor(tags, detectOs());
  const backOrCancel = useBackOrCancel(onCancel, onBack);
  const subtitle = (view: keyof typeof SUBTITLES) =>
    t(SUBTITLES[view][isIssuance ? 'issuance' : 'share'], { appName });

  /** Continue on the app's website, or open the app on this phone. */
  const primaryAction = (): FlowAction | undefined => {
    if (tags.type === 'Redirect') {
      const uri = tags.redirect.uri;
      return {
        label: t('agentStart.redirectButton'),
        onClick: () => window.location.replace(uri),
      };
    }
    if (!appLink) return undefined;
    return {
      label: t('agentStart.openApp', { appName }),
      onClick: () => window.location.replace(appLink),
    };
  };

  return (
    <HostedMobileLayout
      footer={
        <HostedActionRow primary={primaryAction()} secondary={backOrCancel} />
      }
    >
      {tags.type === 'Redirect' ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-6 text-center w-full">
          <div>
            <h2 className="text-2xl font-semibold text-foreground">
              {t('agentStart.title', { appName })}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {subtitle('redirect')}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex flex-1 flex-col w-full">
          <div className="flex flex-col gap-8 py-2">
            <div className="flex flex-col gap-2">
              <h2 className="text-2xl font-semibold text-foreground">
                {t('agentStart.title', { appName })}
              </h2>
              <p className="text-sm text-muted-foreground">
                {subtitle(showQrFallback ? 'qr' : 'openApp')}
              </p>
            </div>

            {showQrFallback ? (
              <HostedQrCodeCard
                qrData={tags.type === 'Qr' ? tags.qr.content : ''}
                appLogo={app?.logo}
                appName={appName}
              />
            ) : (
              <div className="flex flex-col items-center gap-8 rounded-2xl border border-border bg-background p-8">
                <a className="block p-4" href={appLink}>
                  <AppLogo
                    src={app?.logo}
                    name={app?.name ?? appName}
                    className="size-[121px] rounded-[26px] bg-muted p-4"
                    badge={
                      <span className="flex items-center justify-center rounded-full bg-[#a80a2d] p-[3px]">
                        <FontAwesomeIcon
                          icon={faArrowRight}
                          className="size-3.5 text-white"
                        />
                      </span>
                    }
                  />
                </a>
                <CharlieButton
                  variant="outline"
                  pill
                  block
                  onClick={() => setShowQrFallback(true)}
                >
                  <FontAwesomeIcon icon={faQrcode} className="size-4" />
                  {t('agentStart.showQr')}
                </CharlieButton>
              </div>
            )}
          </div>
        </div>
      )}
    </HostedMobileLayout>
  );
}
